import { useMemo } from 'react';

// Node dimensions must stay in sync with the widths/heights
// declared in each node's styles.scss.
const ENTRY_W = 230;
const ENTRY_H = 58;
const RECEIVER_H = 96; // receiver + invitation cards are 330px wide (see node styles)
const ADD_H = 54;
const H_GAP = 150;
const V_GAP = 18;

export default function useSplitFlow({
    receivers,
    invitations = [],
    projection = { receivers: {}, invitations: {} },
    currentUserEmail,
    onChangePercentile,
    onRemoveReceiver,
    onRevokeInvitation,
    onAddReceiver,
}) {
    const nodes = useMemo(() => {
        const childrenX = ENTRY_W + H_GAP;
        const rowStride = RECEIVER_H + V_GAP;
        // Pending invitation rows sit BELOW the active receivers, above the add tile.
        const totalRows = receivers.length + invitations.length;
        const columnHeight = totalRows * rowStride + ADD_H;
        const entryY = Math.max(0, (columnHeight - ENTRY_H) / 2);

        return [
            {
                id: 'entry',
                type: 'entryNode',
                position: { x: 0, y: entryY },
                data: {},
                draggable: false,
                selectable: false,
            },
            ...receivers.map((user, index) => ({
                id: `receiver-${user.id}`,
                type: 'receiverNode',
                position: { x: childrenX, y: index * rowStride },
                data: {
                    user,
                    index,
                    currentUserEmail,
                    projectedPercentile: projection.receivers?.[user.id],
                    onChangePercentile,
                    onRemoveReceiver,
                },
                draggable: false,
                selectable: false,
                // Non-draggable/non-selectable nodes get pointer-events: none
                // from React Flow — re-enable so hover/click reach the cards
                style: { pointerEvents: 'all' },
            })),
            ...invitations.map((invitation, i) => {
                const index = receivers.length + i;
                return {
                    id: `invitation-${invitation.id}`,
                    type: 'invitationNode',
                    position: { x: childrenX, y: index * rowStride },
                    data: {
                        invitation,
                        index,
                        projectedPercentile: projection.invitations?.[invitation.id],
                        onRevokeInvitation,
                    },
                    draggable: false,
                    selectable: false,
                    style: { pointerEvents: 'all' },
                };
            }),
            {
                id: 'add-receiver',
                type: 'addReceiverNode',
                position: { x: childrenX, y: totalRows * rowStride },
                data: { index: totalRows, onAdd: onAddReceiver },
                draggable: false,
                selectable: false,
                style: { pointerEvents: 'all' },
            },
        ];
    }, [receivers, invitations, projection, currentUserEmail, onChangePercentile, onRemoveReceiver, onRevokeInvitation, onAddReceiver]);

    const edges = useMemo(() => [
        ...receivers.map((user, index) => ({
            id: `edge-entry-${user.id}`,
            source: 'entry',
            target: `receiver-${user.id}`,
            type: 'splitFlowEdge',
            // Active split card: carries the live flow pulse.
            data: { index, animated: true },
            style: { stroke: 'var(--surface-8)', strokeWidth: 1.5 },
        })),
        ...invitations.map((invitation, i) => ({
            id: `edge-entry-invitation-${invitation.id}`,
            source: 'entry',
            target: `invitation-${invitation.id}`,
            type: 'splitFlowEdge',
            // Pending arrangement, not a live receiver: dashed, no flow pulse.
            data: { index: receivers.length + i, animated: false },
            style: { stroke: 'var(--surface-8)', strokeWidth: 1.5, strokeDasharray: '5 6' },
        })),
        {
            id: 'edge-entry-add',
            source: 'entry',
            target: 'add-receiver',
            type: 'smoothstep',
            pathOptions: { borderRadius: 24 },
            style: {
                stroke: 'var(--surface-8)',
                strokeWidth: 1.5,
                strokeDasharray: '5 6',
            },
        },
    ], [receivers, invitations]);

    const flowHeight = Math.max(
        300,
        (receivers.length + invitations.length) * (RECEIVER_H + V_GAP) + ADD_H + 64,
    );

    return { nodes, edges, flowHeight };
}
